import { Suspense, lazy, useEffect, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, Outlet, Route, Routes, useLocation, useParams } from 'react-router-dom';

import { IntroCurtain } from '@/components/brand/IntroCurtain';
import { AppShell } from '@/components/layout/AppShell';
import { Spinner } from '@/components/ui/Button';
import { SUPPORTED_LANGUAGES, detectBrowserLanguage, isLanguage } from '@/i18n';
import { useLanguage } from '@/lib/hooks';
import { ROUTE_SEGMENTS, routes, seg, type RouteKey } from '@/routes/paths';
import { useAuth } from '@/state/AuthContext';

// Cada pantalla en su propio fragmento: la primera carga solo trae lo que se
// ve (RNF-02, primera carga util en menos de 2,5 s en 4G).
const LandingPage = lazy(() => import('@/pages/LandingPage'));
const HomePage = lazy(() => import('@/pages/HomePage'));
const CompetitionsPage = lazy(() => import('@/pages/competitions/CompetitionsPage'));
const CompetitionDetailPage = lazy(() => import('@/pages/competitions/CompetitionDetailPage'));
const SubmitPage = lazy(() => import('@/pages/competitions/SubmitPage'));
const SubmissionPage = lazy(() => import('@/pages/competitions/SubmissionPage'));
const ForumPage = lazy(() => import('@/pages/ForumPage'));
const RankingsPage = lazy(() => import('@/pages/rankings/RankingsPage'));
const MyProfilePage = lazy(() => import('@/pages/profile/MyProfilePage'));
const AthleteProfilePage = lazy(() => import('@/pages/profile/AthleteProfilePage'));
const SettingsPage = lazy(() => import('@/pages/profile/SettingsPage'));
const LoginPage = lazy(() => import('@/pages/auth/LoginPage'));
const SignupPage = lazy(() => import('@/pages/auth/SignupPage'));
const RecoverPage = lazy(() => import('@/pages/auth/RecoverPage'));
const VerifyEmailPage = lazy(() => import('@/pages/auth/VerifyEmailPage'));
const LanguagePage = lazy(() => import('@/pages/onboarding/LanguagePage'));
const CreateProfilePage = lazy(() => import('@/pages/onboarding/CreateProfilePage'));
const TutorialPage = lazy(() => import('@/pages/onboarding/TutorialPage'));
const TermsPage = lazy(() => import('@/pages/legal/TermsPage'));
const PrivacyPage = lazy(() => import('@/pages/legal/PrivacyPage'));
const AboutPage = lazy(() => import('@/pages/AboutPage'));
const NotFoundPage = lazy(() => import('@/pages/NotFoundPage'));
const AuthCallbackPage = lazy(() => import('@/pages/auth/AuthCallbackPage'));

export default function App() {
  return (
    <Suspense fallback={<FullPageSpinner />}>
      {/*
        El telon va FUERA de las rutas: se pinta por encima mientras la
        aplicacion se monta detras, asi que cuando se retira ya esta lista y no
        ha costado un solo milisegundo de espera.
      */}
      <IntroCurtain />

      <Routes>
        {/* Sin idioma en la URL se usa el del navegador (RF-LANG-02). */}
        {/*
          La vuelta del proveedor va sin prefijo de idioma y ANTES que nada:
          tiene que quedarse quieta con el `?code=` en la URL para que la
          biblioteca de Supabase lo canjee. Cualquier redireccion aqui se lo
          lleva por delante.
        */}
        <Route path="/auth/callback" element={<AuthCallbackPage />} />

        {/* La raiz conserva la cadena de consulta: si algun dia un proveedor
            vuelve aqui en vez de al callback, el codigo sobrevive al salto. */}
        <Route
          path="/"
          element={
            <Navigate
              to={{ pathname: `/${detectBrowserLanguage()}`, search: window.location.search }}
              replace
            />
          }
        />

        <Route path="/:lang" element={<LanguageGate />}>
          {/* --- Autenticacion, sin el armazon de la aplicacion ------------- */}
          {localized('auth', (authPath, l) => (
            <Route key={authPath} path={authPath}>
              <Route index element={<Navigate to={routes.login(l)} replace />} />
              <Route path={seg('login', l)} element={<LoginPage />} />
              <Route path={seg('signup', l)} element={<SignupPage />} />
              <Route path={seg('recover', l)} element={<RecoverPage />} />
              <Route path={seg('verify', l)} element={<VerifyEmailPage />} />
            </Route>
          ))}

          {/* --- Onboarding: pantalla completa, sin distracciones ----------- */}
          {localized('onboarding', (onboardingPath, l) => (
            <Route key={onboardingPath} path={onboardingPath}>
              <Route
                index
                element={
                  <RequireAuth>
                    <CreateProfilePage />
                  </RequireAuth>
                }
              />
              <Route
                path={seg('language', l)}
                element={
                  <RequireAuth>
                    <LanguagePage />
                  </RequireAuth>
                }
              />
              <Route
                path={seg('tutorial', l)}
                element={
                  <RequireAuth>
                    <TutorialPage />
                  </RequireAuth>
                }
              />
            </Route>
          ))}

          {/* --- Aplicacion -------------------------------------------------- */}
          <Route element={<AppShell />}>
            <Route index element={<HomeOrLanding />} />

            {localized('competitions', (competitionsPath, l) => (
              <Route key={competitionsPath} path={competitionsPath}>
                <Route index element={<CompetitionsPage />} />
                <Route path=":slug" element={<CompetitionDetailPage />} />
                {/* Marca aprobada en publico: video, reacciones y comentarios. */}
                <Route path={`:slug/${seg('marks', l)}/:id`} element={<SubmissionPage />} />
                <Route
                  path={`:slug/${seg('submit', l)}`}
                  element={
                    <RequireProfile>
                      <SubmitPage />
                    </RequireProfile>
                  }
                />
              </Route>
            ))}

            {localized('rankings', (rankingsPath, l) => (
              <Route key={rankingsPath} path={rankingsPath}>
                <Route index element={<Navigate to={routes.rankingsGlobal(l)} replace />} />
                <Route path={seg('global', l)} element={<RankingsPage scope="GLOBAL" />} />
                <Route path={seg('local', l)} element={<RankingsPage scope="LOCAL" />} />
              </Route>
            ))}

            {localized('athletes', (athletesPath) => (
              <Route key={athletesPath} path={`${athletesPath}/:alias`} element={<AthleteProfilePage />} />
            ))}

            {localized('profile', (profilePath) => (
              <Route
                key={profilePath}
                path={profilePath}
                element={
                  <RequireProfile>
                    <MyProfilePage />
                  </RequireProfile>
                }
              />
            ))}

            {localized('settings', (settingsPath) => (
              <Route
                key={settingsPath}
                path={settingsPath}
                element={
                  <RequireProfile>
                    <SettingsPage />
                  </RequireProfile>
                }
              />
            ))}

            {/*
              El panel YA NO vive aqui: tiene su propia direccion y su propio
              paquete (admin.html / src/AdminApp.tsx). Quien llegue a /admin
              desde el sitio de atletas se encuentra la pagina de siempre para
              rutas que no existen, que es exactamente lo que queremos: desde
              aqui el panel no se descubre.
            */}

            {/* El foro se lee sin cuenta; para escribir hace falta perfil. */}
            {localized('forum', (forumPath) => (
              <Route key={forumPath} path={forumPath} element={<ForumPage />} />
            ))}

            {localized('terms', (termsPath) => (
              <Route key={termsPath} path={termsPath} element={<TermsPage />} />
            ))}
            {localized('privacy', (privacyPath) => (
              <Route key={privacyPath} path={privacyPath} element={<PrivacyPage />} />
            ))}
            {/* Quien hay detras de CaliPeak y formulario de contacto: publica. */}
            {localized('about', (aboutPath) => (
              <Route key={aboutPath} path={aboutPath} element={<AboutPage />} />
            ))}

            <Route path="*" element={<NotFoundPage />} />
          </Route>
        </Route>

        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Suspense>
  );
}

/**
 * Genera una rama de rutas por cada forma distinta que toma un segmento
 * (RF-LANG-09: `/es/competiciones` y `/en/competitions` son la misma
 * pantalla). Cuando el segmento coincide en ambos idiomas —`rankings`,
 * `admin`— se emite una sola vez, para no registrar rutas duplicadas.
 */
function localized(
  key: RouteKey,
  render: (path: string, language: (typeof SUPPORTED_LANGUAGES)[number]) => ReactElement,
): ReactElement[] {
  const emitted = new Set<string>();
  const result: ReactElement[] = [];

  for (const language of SUPPORTED_LANGUAGES) {
    const path = ROUTE_SEGMENTS[key][language];
    if (emitted.has(path)) continue;
    emitted.add(path);
    result.push(render(path, language));
  }

  return result;
}

/**
 * Sincroniza i18next con el idioma de la URL. La URL manda: un enlace
 * compartido a `/en/...` se ve en ingles aunque el navegador este en espanol.
 */
// Se exporta para que la aplicacion del panel, que vive en otro dominio y
// tiene su propio router, reutilice la misma guardia de idioma.
export function LanguageGate() {
  const { lang } = useParams();
  const { i18n } = useTranslation();

  useEffect(() => {
    if (!isLanguage(lang)) return;
    if (i18n.resolvedLanguage !== lang) void i18n.changeLanguage(lang);
    document.documentElement.lang = lang;
  }, [lang, i18n]);

  if (!isLanguage(lang)) return <NotFoundPage />;
  return <Outlet />;
}

function HomeOrLanding() {
  const { isAuthenticated, loading } = useAuth();
  if (loading) return <FullPageSpinner />;
  return isAuthenticated ? <HomePage /> : <LandingPage />;
}

// ---------------------------------------------------------------------------
// Guardias de ruta
// ---------------------------------------------------------------------------

function RequireAuth({ children }: { children: ReactElement }) {
  const { isAuthenticated, loading } = useAuth();
  const language = useLanguage();
  const location = useLocation();

  if (loading) return <FullPageSpinner />;
  if (!isAuthenticated) {
    // Se recuerda a donde iba, para volver alli tras entrar.
    return <Navigate to={routes.login(language)} state={{ from: location.pathname }} replace />;
  }
  return children;
}

/** Exige sesion y onboarding terminado: sin sexo y peso no hay categoria. */
function RequireProfile({ children }: { children: ReactElement }) {
  const { isAuthenticated, hasProfile, loading } = useAuth();
  const language = useLanguage();
  const location = useLocation();

  if (loading) return <FullPageSpinner />;
  if (!isAuthenticated) {
    return <Navigate to={routes.login(language)} state={{ from: location.pathname }} replace />;
  }
  if (!hasProfile) return <Navigate to={routes.onboardingLanguage(language)} replace />;
  return children;
}

// Exportada por el mismo motivo que LanguageGate.
export function RequireModerator({ children }: { children: ReactElement }) {
  const { isModerator, loading, isAuthenticated } = useAuth();
  const language = useLanguage();

  if (loading) return <FullPageSpinner />;
  if (!isAuthenticated) return <Navigate to={routes.login(language)} replace />;
  if (!isModerator) return <Navigate to={routes.home(language)} replace />;
  return children;
}

export function FullPageSpinner() {
  const { t } = useTranslation();
  return (
    <div className="grid min-h-[50dvh] place-items-center" role="status" aria-live="polite">
      <Spinner className="h-8 w-8 text-ink" />
      <span className="sr-only">{t('common.loading')}</span>
    </div>
  );
}
